import { axiosInstance } from '.'
import { SearchParams, NewOpportunity } from '../lib/types'
import {
  UPLOAD_FILE,
  PREVIEW_FILE,
  DELETE_PROJECT,
  UPLOAD_FUND_ALLOCATION,
  UPLOAD_QUARTERLY_REPORT_JOB,
  SEARCH_OPPORTUNITIES,
  GET_ALL_OPPORTUNITIES,
  GET_ASSET_CLASSES,
  GET_SECTORS,
  GET_OPPORTUNITY_BY_ID,
  CREATE_NEW_OPPORTUNITY,
  UPDATE_OPPORTUNITY,
  UPLOAD_UNIT_PRICE_UPDATE,
} from './endpoints'
import { removeEmptyValueFromObject, sanitizeOpportunityPayload } from '../lib/utils/helpers'
import { CMS_DEPRECATED_OPPORTUNITY_FIELDS } from '../pages/opportunity-form/opportunity-form-cms-fields'

export const opportunityServices = {
  postUploadFiles: async (filesData: File) => {
    try {
      const formData = new FormData()
      formData.append('file', filesData)
      return await axiosInstance.post(UPLOAD_FILE, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    } catch (error: any) {
      return error.response
    }
  },
  postUploadFundAllocationFiles: async (filesData: any) => {
    try {
      return await axiosInstance.post(UPLOAD_FUND_ALLOCATION, filesData)
    } catch (error: any) {
      return error.response
    }
  },
  postQuarterlyReportFiles: async (filesData: any) => {
    try {
      return await axiosInstance.post(UPLOAD_QUARTERLY_REPORT_JOB, filesData)
    } catch (error: any) {
      return error.response
    }
  },

  deleteProject: async (id: number) => {
    try {
      const data = await axiosInstance.delete(`${DELETE_PROJECT}/${id}`)
      return data
    } catch (error: any) {
      return error.response
    }
  },
  previewFile: async (fileKey: string) => {
    try {
      const data = await axiosInstance.get(`${PREVIEW_FILE}/${fileKey}`)
      return data
    } catch (error: any) {
      return error.response
    }
  },

  //v1
  getAllOpportunities: async () => {
    try {
      const data = await axiosInstance.get(GET_ALL_OPPORTUNITIES)
      return {
        opportunities: data.data.opportunities || data.data,
        status: data.status,
      }
    } catch (error: any) {
      return error.response
    }
  },

  getSearchOpportunities: async (params: SearchParams) => {
    try {
      const data = await axiosInstance.get(`${SEARCH_OPPORTUNITIES}`, {
        params: removeEmptyValueFromObject(params),
      })
      const searchedOpportunities = {
        opportunities: data.data.opportunities,
        pagination: {
          count: data.data.count,
          limit: data.data.limit,
          offset: data.data.offset,
          total: data.data.total,
        },
        status: data.status,
      }
      return searchedOpportunities
    } catch (error: any) {
      return error.response
    }
  },

  getAssetClasses: async () => {
    try {
      const data = await axiosInstance.get(GET_ASSET_CLASSES)
      return data
    } catch (error: any) {
      return error.response
    }
  },

  getSectors: async () => {
    try {
      const data = await axiosInstance.get(GET_SECTORS)
      return data
    } catch (error: any) {
      return error.response
    }
  },

  getOpportunityById: async (id: string, isFormik = true) => {
    try {
      const data = await axiosInstance.get(`${GET_OPPORTUNITY_BY_ID}/${id}`)
      let project
      if (isFormik && data.data) {
        project = {
          ...data.data,
        }
      }
      if (!isFormik) {
        project = {
          ...data.data,
          committedAmount:
            (data.data.total_investment_amount && data.data.total_investment_amount.toString()) ??
            '0',
          irr: data.data.investment_return.toString() ?? '',
          overallRaiseGoal: data.data.raise_goal.toString() ?? '',
          raiseGoal: data.data.raise_goal.toString() ?? '',
          ticketSize: data.data.ticket_size.toString() ?? '',
        }
      }
      return { data: project, status: data.status }
    } catch (error: any) {
      return error.response
    }
  },

  createOpportunity: async (opportunityDetailsData: NewOpportunity) => {
    try {
      const payload = sanitizeOpportunityPayload(opportunityDetailsData, [])

      const data: any = await axiosInstance.post(CREATE_NEW_OPPORTUNITY, payload)
      const opportunityData = {
        ...data.data,
      }
      return { data: opportunityData, status: data.status }
    } catch (error: any) {
      return error.response
    }
  },

  updateOpportunity: async (opportunityDetailsData: NewOpportunity, id: string) => {
    try {
      const payload = sanitizeOpportunityPayload(opportunityDetailsData, [
        'id',
        'created_at',
        'updated_at',
        'no_of_investors',
        'total_investment_amount',
        'updates',
        ...CMS_DEPRECATED_OPPORTUNITY_FIELDS,
      ])

      return await axiosInstance.patch(`${UPDATE_OPPORTUNITY}/${id}`, payload)
    } catch (error: any) {
      return error.response
    }
  },

  updateOpportunityDisplayOrder: async (id: number, display_order: number | null) => {
    try {
      return await axiosInstance.patch(`${UPDATE_OPPORTUNITY}/${id}`, { display_order })
    } catch (error: any) {
      return error.response
    }
  },

  postUnitPriceUpdateFile: async (payload: FormData) => {
    try {
      const data: any = await axiosInstance.post(UPLOAD_UNIT_PRICE_UPDATE, payload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      if (data) return data
    } catch (error: any) {
      return error.response
    }
  },
}
